import React, { useState, useEffect, useRef } from "react";
import EditIcon from "@mui/icons-material/Edit";
import SearchIcon from "@mui/icons-material/Search";
import {
  Modal,
  Box,
  Button,
  Fab,
  TextField,
  Tooltip,
  Autocomplete,
  Radio,
  RadioGroup,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  Grid,
  Typography,
  Divider,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import axios from "axios";
import {
  DataGrid,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
} from "@mui/x-data-grid";
import "./Item.scss";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import ToggleOffIcon from "@mui/icons-material/ToggleOff";
import { format } from "date-fns";
import DisplayDate from "../../components/date/DisplayDate";
import ApiDatePickerField from "../../components/date/ApiDatePickerField";
import { CheckToken } from "../login/AccessToken";
import { useHistory } from "react-router-dom/cjs/react-router-dom";
import { toast } from "react-toastify";
import SideNavbar from "./SideNavbar";
import NoDataPage from "./NoDataPage";
import NavigationIcon from "@mui/icons-material/Navigation";
// import Icon from "../doctor-console/Utils/Icons";
import { ReactComponent as CalendarIcon } from "./DummyIcons/calendar-silhouette 2.svg";

import SaveAlt from "@mui/icons-material/SaveAlt";
import { CSVLink } from "react-csv";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { ReactComponent as CLoseIcon } from "./DummyIcons/CloseButton.svg";
import {
  getSlipDepartments,
  getServiceDepartment,
  setServiceDepartment,
} from "./departmentSlipApi";

const DOCTOR_FEE_TYPES = {
  CONSULTATION: "CONSULTATION",
  FOLLOW_UP: "FOLLOW_UP",
  REPORT_VISIT: "REPORT_VISIT",
};

const DOCTOR_FEE_TYPE_SUFFIX = {
  [DOCTOR_FEE_TYPES.CONSULTATION]: "Consultation fees",
  [DOCTOR_FEE_TYPES.FOLLOW_UP]: "Follow up fees",
  [DOCTOR_FEE_TYPES.REPORT_VISIT]: "Report Visit fees",
};

const DOCTOR_FEE_TYPE_LABEL = {
  [DOCTOR_FEE_TYPES.CONSULTATION]: "Consultation Fee",
  [DOCTOR_FEE_TYPES.FOLLOW_UP]: "Follow up Fee",
  [DOCTOR_FEE_TYPES.REPORT_VISIT]: "Report Visit Fee",
};

const NAME_SUFFIX_MAX_LENGTH = 50;

function doctorAllowsMultipleFees(doctor) {
  const value = doctor?.multiple_fees;
  return value === true || value === "true" || value === 1 || value === "1";
}

function buildDoctorFeeServiceName(doctorName, feeType) {
  const name = String(doctorName || "").trim();
  const suffix =
    DOCTOR_FEE_TYPE_SUFFIX[feeType] ||
    DOCTOR_FEE_TYPE_SUFFIX[DOCTOR_FEE_TYPES.CONSULTATION];
  return name ? `${name} ${suffix}` : suffix;
}

function composeDoctorFeeServiceName(baseName, customSuffix) {
  const base = String(baseName || "").trim();
  const extra = String(customSuffix || "")
    .trim()
    .slice(0, NAME_SUFFIX_MAX_LENGTH);
  if (!extra) return base;
  return `${base} ${extra}`;
}

function splitDoctorFeeSuffix(fullName, doctorName, feeType) {
  const baseName = buildDoctorFeeServiceName(doctorName, feeType);
  const full = String(fullName || "").trim();
  if (!baseName || !full) return { baseName: full, suffix: "" };
  const baseLower = baseName.toLowerCase();
  const fullLower = full.toLowerCase();
  if (!fullLower.startsWith(baseLower)) {
    return { baseName: full, suffix: "" };
  }
  let rest = full.slice(baseName.length).trim();
  rest = rest.replace(/^[-–—]\s*/, "").trim();
  return {
    baseName,
    suffix: rest.slice(0, NAME_SUFFIX_MAX_LENGTH),
  };
}

function splitDoctorFeeSuffixFromStoredName(fullName, feeType) {
  const typeSuffix =
    DOCTOR_FEE_TYPE_SUFFIX[feeType] ||
    DOCTOR_FEE_TYPE_SUFFIX[DOCTOR_FEE_TYPES.CONSULTATION];
  const full = String(fullName || "").trim();
  const idx = full.toLowerCase().indexOf(String(typeSuffix).toLowerCase());
  if (idx < 0) return { baseName: full, suffix: "" };
  const baseName = full.slice(0, idx + typeSuffix.length).trim();
  let rest = full.slice(idx + typeSuffix.length).trim();
  rest = rest.replace(/^[-–—]\s*/, "").trim();
  return {
    baseName,
    suffix: rest.slice(0, NAME_SUFFIX_MAX_LENGTH),
  };
}

function getRowDoctorId(row) {
  if (!row) return "";
  const raw =
    row.doctor_id ??
    row.doctorId ??
    row.assigned_doctor_id ??
    row.user_id ??
    row.doctor?.doctor_id ??
    row.doctor?.id;
  if (raw == null || raw === "") return "";
  return String(raw);
}

function getDoctorRecordId(doctor) {
  if (!doctor) return "";
  const raw = doctor.doctor_id ?? doctor.id ?? doctor.user_id;
  if (raw == null || raw === "") return "";
  return String(raw);
}

function findDoctorByServiceName(doctorList, serviceName) {
  const full = String(serviceName || "")
    .trim()
    .toLowerCase();
  if (!full || !Array.isArray(doctorList)) return null;
  let best = null;
  let bestLen = -1;
  doctorList.forEach((doctor) => {
    const name = String(doctor.name || "")
      .trim()
      .toLowerCase();
    if (!name) return;
    if (full === name || full.startsWith(`${name} `)) {
      if (name.length > bestLen) {
        best = doctor;
        bestLen = name.length;
      }
    }
  });
  return best;
}

function resolveDoctorForService(doctorList, row, serviceName) {
  const id = getRowDoctorId(row);
  if (id) {
    const byId = (doctorList || []).find(
      (doctor) => getDoctorRecordId(doctor) === id,
    );
    if (byId) return byId;
  }
  return findDoctorByServiceName(doctorList, serviceName);
}

function inferFeeTypeFromServiceName(serviceName) {
  const n = String(serviceName || "").toLowerCase();
  if (n.includes("follow up fee")) return DOCTOR_FEE_TYPES.FOLLOW_UP;
  if (n.includes("report visit fee")) return DOCTOR_FEE_TYPES.REPORT_VISIT;
  if (n.includes("consultation fee")) return DOCTOR_FEE_TYPES.CONSULTATION;
  return DOCTOR_FEE_TYPES.CONSULTATION;
}

function isDoctorFeeServiceName(serviceName) {
  const n = String(serviceName || "").toLowerCase();
  return (
    n.includes("consultation fee") ||
    n.includes("follow up fee") ||
    n.includes("report visit fee")
  );
}

function getFeeTypeLabelFromServiceName(serviceName) {
  if (!isDoctorFeeServiceName(serviceName)) return "—";
  return DOCTOR_FEE_TYPE_LABEL[inferFeeTypeFromServiceName(serviceName)];
}

const Item = () => {
  const today = new Date().toISOString().split("T")[0];
  const Token = () => {
    const check = CheckToken();
    console.log(check);
    setAuthToken(sessionStorage.getItem("accessToken"));
    return check;
  };

  const {
    control,
    handleSubmit,
    reset,
    register,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = useForm();
  const {
    control: control1,
    handleSubmit: handleSubmit1,
    reset: reset1,
    register: register1,
    formState: { errors: errors1 },
    setValue: setValue1,
    getValues: getValues1,
  } = useForm();
  const [authToken, setAuthToken] = useState(
    sessionStorage.getItem("accessToken") || "",
  );
  const [facilityName, setFacilityName] = useState(
    sessionStorage.getItem("facility_name") || "",
  );

  const REACT_APP_BACKEND_ROUTE_URL = process.env.REACT_APP_BACKEND_ROUTE_URL;

  const headers = {
    Authorization: `Bearer ${authToken}`,
  };

  const [isModalOpen, setModalOpen] = useState(false);
  const [serviceList, setServiceList] = useState([]);
  const [editItemId, setEditItemId] = useState(null);
  const [editAllowsNameSuffix, setEditAllowsNameSuffix] = useState(false);
  const [doctorList, setDoctorList] = useState([]);
  const [isDoctorListLoading, setDoctorListLoading] = useState(false);
  // Ref, not state: keeps doctorList out of the fetch effect's deps, so loading
  // the list cannot re-trigger the effect and cancel its own in-flight request.
  const areDoctorsLoadedRef = useRef(false);
  const previousDoctorIdRef = useRef("");
  const previousFeeTypeRef = useRef("");
  const editServiceRowRef = useRef(null);
  const didHydrateEditFeeNameRef = useRef(false);

  const isDoctorAppointmentCharge = watch("is_doctor_appointment_charge");
  const selectedDoctorId = watch("doctor_id");
  const selectedFeeType = watch("fee_type") || DOCTOR_FEE_TYPES.CONSULTATION;
  const serviceName = watch("name");
  const isDoctorFeeName = isDoctorFeeServiceName(serviceName);
  const selectedDoctorForFee = doctorList.find(
    (doctor) => getDoctorRecordId(doctor) === String(selectedDoctorId),
  );
  const allowsNameSuffix = Boolean(
    isDoctorAppointmentCharge &&
      (doctorAllowsMultipleFees(selectedDoctorForFee) ||
        editAllowsNameSuffix),
  );
  /** Auto doctor-fee name is always locked; multiple_fees doctors edit suffix only. */
  const isNameLocked = Boolean(isDoctorAppointmentCharge || isDoctorFeeName);
  const [deactivationReason, setDeactivationReason] = useState("");
  const [isDeactivationModalOpen, setDeactivationModalOpen] = useState(false);
  const [showColumns, setShowColumns] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [serviceLoading, setServiceLoading] = useState(false);
  const [serviceSearchText, setServiceSearchText] = useState("");
  const [debouncedServiceSearch, setDebouncedServiceSearch] = useState("");
  const [servicePagination, setServicePagination] = useState({
    page: 1,
    limit: 10,
    total_records: 0,
    total_pages: 0,
    has_next: false,
    has_prev: false,
    next_page: null,
    prev_page: null,
  });
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  const history = useHistory();

  // Department slip routing for the service in the modal. Choice values: ""
  // (none), "med:<department_id>", "phys:<physical_department_id>", "new".
  const [slipDepts, setSlipDepts] = useState(null);
  const [deptChoice, setDeptChoice] = useState("");
  const [initialDeptChoice, setInitialDeptChoice] = useState("");
  const [newDeptName, setNewDeptName] = useState("");

  useEffect(() => {
    if (!isModalOpen) return;
    let alive = true;
    setSlipDepts(null);
    setDeptChoice("");
    setInitialDeptChoice("");
    setNewDeptName("");
    (async () => {
      try {
        const depts = await getSlipDepartments();
        let choice = "";
        if (editItemId) {
          const pid = await getServiceDepartment(editItemId);
          if (pid != null) {
            const med = depts.medical.find(
              (d) => String(d.physicalDepartmentId) === String(pid),
            );
            choice = med ? `med:${med.id}` : `phys:${pid}`;
          }
        }
        if (!alive) return;
        setSlipDepts(depts);
        setDeptChoice(choice);
        setInitialDeptChoice(choice);
      } catch (err) {
        if (alive) setSlipDepts({ medical: [], physical: [], error: err.message });
      }
    })();
    return () => {
      alive = false;
    };
  }, [isModalOpen, editItemId]);

  const saveSlipDepartment = async (medicalServiceId) => {
    const changed = deptChoice === "new" || deptChoice !== initialDeptChoice;
    if (!changed || !medicalServiceId) return;
    let choice = { physical_department_id: null };
    if (deptChoice === "new") {
      choice = { new_physical_department_name: newDeptName.trim() };
    } else if (deptChoice.startsWith("med:")) {
      choice = { medical_department_id: deptChoice.slice(4) };
    } else if (deptChoice.startsWith("phys:")) {
      const phys = slipDepts?.physical.find(
        (d) => String(d.id) === deptChoice.slice(5),
      );
      choice = { physical_department_id: phys ? phys.id : deptChoice.slice(5) };
    }
    try {
      await setServiceDepartment(medicalServiceId, choice);
    } catch (err) {
      toast.error(`Service saved, but slip department was not set: ${err.message}`);
    }
  };

  useEffect(() => {
    if (
      sessionStorage.getItem("accessToken") == undefined &&
      sessionStorage.getItem("accessToken") == null
    ) {
      history.push("/login");
    }
  }, []);

  const fetchServiceList = async ({
    page,
    limit,
    searchText = debouncedServiceSearch,
    isMounted = true,
  } = {}) => {
    if (!authToken) return;

    const safePage = Number.isFinite(page) ? page : 1;
    const safeLimit = Number.isFinite(limit) ? limit : 10;

    const params = {
      is_pagination_required: true,
      page: safePage,
      limit: safeLimit,
    };
    if (searchText?.trim()?.length >= 2) {
      params.search = searchText.trim();
    }

    setServiceLoading(true);
    try {
      const res = await axios.get(
        `${REACT_APP_BACKEND_ROUTE_URL}get_all_medical_services/`,
        {
          headers: headers,
          params,
        },
      );

      if (!isMounted) return;

      const pg = res?.data?.pagination || {};
      setServicePagination({
        page: Number(pg.page) || safePage,
        limit: Number(pg.limit) || safeLimit,
        total_records: Number(pg.total_records) || 0,
        total_pages: Number(pg.total_pages) || 0,
        has_next: Boolean(pg.has_next),
        has_prev: Boolean(pg.has_prev),
        next_page: pg.next_page ?? null,
        prev_page: pg.prev_page ?? null,
      });

      const serviceListData = Array.isArray(res?.data?.service_list)
        ? res.data.service_list
        : [];
      const servicesWithId = serviceListData.map((service) => ({
        ...service,
        id: service.medical_service_id ?? service.id,
        status: getStatusText(service.expired, service.active),
      }));
      setServiceList(servicesWithId);
    } catch (error) {
      console.error("Error fetching service list:", error);
    } finally {
      if (isMounted) setServiceLoading(false);
    }
  };

  const getStatusText = (expired, active) => {
    if (!expired && active) {
      return "Active";
    } else {
      return "Inactive";
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedServiceSearch(serviceSearchText.trim());
    }, 500);

    return () => clearTimeout(timer);
  }, [serviceSearchText]);

  useEffect(() => {
    setPaginationModel((prev) =>
      prev.page === 0 ? prev : { ...prev, page: 0 },
    );
  }, [debouncedServiceSearch]);

  useEffect(() => {
    let isMounted = true;
    const trimmedDebouncedSearch = debouncedServiceSearch.trim();
    if (trimmedDebouncedSearch.length === 1) {
      return () => {
        isMounted = false;
      };
    }
    const backendPage = paginationModel.page + 1;
    const backendLimit = paginationModel.pageSize;
    fetchServiceList({
      page: backendPage,
      limit: backendLimit,
      searchText: trimmedDebouncedSearch,
      isMounted,
    });
    return () => {
      isMounted = false;
    };
  }, [
    authToken,
    paginationModel.page,
    paginationModel.pageSize,
    debouncedServiceSearch,
  ]);

  useEffect(() => {
    if (!isModalOpen || !authToken || areDoctorsLoadedRef.current) {
      return;
    }

    let isActive = true;
    setDoctorListLoading(true);

    axios
      .get(
        `${REACT_APP_BACKEND_ROUTE_URL}get_doctors_based_on_facility_user/`,
        {
          headers: { Authorization: `Bearer ${authToken}` },
        },
      )
      .then((res) => {
        if (!isActive) return;
        if (res?.data?.status === "Success") {
          const rawList = res?.data?.data ?? [];
          setDoctorList(
            rawList.map((doctor) => ({
              ...doctor,
              doctor_id: getDoctorRecordId(doctor) || doctor.doctor_id,
            })),
          );
          areDoctorsLoadedRef.current = true;
        } else {
          toast.error(res?.data?.message || "Unable to load the doctor list");
        }
      })
      .catch((error) => {
        console.error("Error fetching doctor list:", error);
        if (isActive) toast.error("Unable to load the doctor list");
      })
      .finally(() => {
        if (isActive) setDoctorListLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, [isModalOpen, authToken, REACT_APP_BACKEND_ROUTE_URL]);

  useEffect(() => {
    // On edit, keep the existing fee-service name fixed (don't rewrite from doctor/type).
    if (editItemId) return;
    if (!isDoctorAppointmentCharge || !selectedDoctorId) {
      previousDoctorIdRef.current = "";
      previousFeeTypeRef.current = "";
      setValue("name_suffix", "");
      return;
    }
    const selectedDoctor = doctorList.find(
      (doctor) => String(doctor.doctor_id) === String(selectedDoctorId),
    );
    const doctorChanged =
      String(previousDoctorIdRef.current) !== String(selectedDoctorId);
    const feeTypeChanged = previousFeeTypeRef.current !== selectedFeeType;
    previousDoctorIdRef.current = selectedDoctorId;
    previousFeeTypeRef.current = selectedFeeType;
    if (selectedDoctor && (doctorChanged || feeTypeChanged)) {
      setValue(
        "name",
        buildDoctorFeeServiceName(selectedDoctor.name, selectedFeeType),
      );
    }
    if (doctorChanged || !doctorAllowsMultipleFees(selectedDoctor)) {
      setValue("name_suffix", "");
    }
  }, [
    isDoctorAppointmentCharge,
    selectedDoctorId,
    selectedFeeType,
    doctorList,
    setValue,
    editItemId,
  ]);

  // Edit can open before doctors finish loading; fill doctor_id from the
  // service name and split full name into locked base + suffix.
  useEffect(() => {
    if (!editItemId || !isDoctorAppointmentCharge) {
      return;
    }
    const row = editServiceRowRef.current;
    if (!row || !doctorList.length) return;
    const doctor = resolveDoctorForService(doctorList, row, row.name);
    if (!doctor) return;
    const doctorId = getDoctorRecordId(doctor);
    if (doctorId && String(getValues("doctor_id") || "") !== doctorId) {
      setValue("doctor_id", doctorId);
      previousDoctorIdRef.current = doctorId;
    }
    if (didHydrateEditFeeNameRef.current) return;
    if (!doctorAllowsMultipleFees(doctor)) return;
    const feeType =
      getValues("fee_type") ||
      inferFeeTypeFromServiceName(row.name) ||
      DOCTOR_FEE_TYPES.CONSULTATION;
    const split = splitDoctorFeeSuffix(
      String(row.name || ""),
      doctor.name,
      feeType,
    );
    setValue("name", split.baseName);
    setValue("name_suffix", split.suffix);
    didHydrateEditFeeNameRef.current = true;
    setEditAllowsNameSuffix(true);
  }, [
    doctorList,
    editItemId,
    isDoctorAppointmentCharge,
    getValues,
    setValue,
  ]);

  const handleAddItem = () => {
    setModalOpen(true);
    previousDoctorIdRef.current = "";
    previousFeeTypeRef.current = "";
    editServiceRowRef.current = null;
    didHydrateEditFeeNameRef.current = false;
    setEditAllowsNameSuffix(false);
    reset({
      name: "",
      description: "",
      fee: "",
      expiry_date: "",
      is_doctor_appointment_charge: false,
      doctor_id: "",
      fee_type: DOCTOR_FEE_TYPES.CONSULTATION,
      name_suffix: "",
    });
  };

  const handleEditItem = (row) => {
    setModalOpen(true);
    editServiceRowRef.current = row;
    const isCharge =
      Boolean(row.is_doctor_appointment_charge) ||
      isDoctorFeeServiceName(row.name);
    const feeType = inferFeeTypeFromServiceName(row.name);
    const doctor = resolveDoctorForService(doctorList, row, row.name);
    const doctorId = doctor
      ? getDoctorRecordId(doctor)
      : getRowDoctorId(row);
    const storedSplit = splitDoctorFeeSuffixFromStoredName(
      String(row.name || ""),
      feeType,
    );
    const shouldSplitName =
      isCharge &&
      (doctorAllowsMultipleFees(doctor) || Boolean(storedSplit.suffix));
    let name = String(row.name || "");
    let name_suffix = "";
    if (shouldSplitName) {
      if (doctor) {
        const split = splitDoctorFeeSuffix(name, doctor.name, feeType);
        name = split.baseName;
        name_suffix = split.suffix;
      } else {
        name = storedSplit.baseName;
        name_suffix = storedSplit.suffix;
      }
    }
    didHydrateEditFeeNameRef.current = shouldSplitName;
    setEditAllowsNameSuffix(shouldSplitName);
    previousDoctorIdRef.current = doctorId;
    previousFeeTypeRef.current = feeType;
    reset({
      name,
      description: row.description,
      fee: row.fee,
      expiry_date: row.expiry_date,
      is_doctor_appointment_charge: isCharge,
      doctor_id: doctorId,
      fee_type: feeType,
      name_suffix,
    });
    setEditItemId(row.medical_service_id);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    previousDoctorIdRef.current = "";
    previousFeeTypeRef.current = "";
    editServiceRowRef.current = null;
    didHydrateEditFeeNameRef.current = false;
    setEditAllowsNameSuffix(false);
    areDoctorsLoadedRef.current = false;
    reset({
      name: "",
      description: "",
      fee: "",
      expiry_date: "",
      is_doctor_appointment_charge: false,
      doctor_id: "",
      fee_type: DOCTOR_FEE_TYPES.CONSULTATION,
      name_suffix: "",
    });
    setEditItemId(null);
  };

  const handleSaveItem = (data) => {
    if (!authToken) {
      toast.error("Session expired. Please sign in again.");
      return;
    }
    // Slip department is mandatory except for doctor appointment charges,
    // which never produce a department slip.
    if (!getValues("is_doctor_appointment_charge")) {
      if (!slipDepts || slipDepts.error) {
        toast.error("Slip departments could not be loaded. Please try again.");
        return;
      }
      if (!deptChoice) {
        toast.error("Slip department is required");
        return;
      }
      if (deptChoice === "new" && !newDeptName.trim()) {
        toast.error("Enter a name for the new department");
        return;
      }
    }

    const requestConfig = {
      headers: headers,
    };

    if (editItemId) {
      // Disabled fields (consultation fee name/doctor/checkbox) are omitted by RHF —
      // pull them from getValues so update still sends the locked values.
      const allValues = getValues();
      const doctorForFee = doctorList.find(
        (doctor) =>
          String(doctor.doctor_id) ===
          String(allValues.doctor_id || data.doctor_id),
      );
      const isCharge = Boolean(
        allValues.is_doctor_appointment_charge ??
          data.is_doctor_appointment_charge,
      );
      const feeType =
        allValues.fee_type ||
        data.fee_type ||
        inferFeeTypeFromServiceName(allValues.name ?? data.name);
      const suffix = String(
        allValues.name_suffix ?? data.name_suffix ?? "",
      ).trim();
      const baseName =
        isCharge && doctorForFee
          ? buildDoctorFeeServiceName(doctorForFee.name, feeType)
          : String((allValues.name ?? data.name) || "").trim();
      const serviceNameToSave =
        isCharge && (doctorAllowsMultipleFees(doctorForFee) || suffix)
          ? composeDoctorFeeServiceName(baseName, suffix)
          : baseName;
      const requestData = {
        medical_service_id: editItemId,
        hip_id: facilityName,
        ...data,
        name: serviceNameToSave,
        is_doctor_appointment_charge: isCharge,
        ...(allValues.doctor_id || data.doctor_id
          ? { doctor_id: String(allValues.doctor_id || data.doctor_id) }
          : {}),
      };
      delete requestData.name_suffix;

      axios
        .post(
          `${REACT_APP_BACKEND_ROUTE_URL}update_medical_service/`,
          JSON.stringify(requestData),
          requestConfig,
        )
        .then(async (res) => {
          toast.success(res.data.message);
          await saveSlipDepartment(editItemId);
          setModalOpen(false);
          reset();
          setEditItemId(null);
          fetchServiceList({
            page: paginationModel.page + 1,
            limit: paginationModel.pageSize,
          });
        })
        .catch((error) => {
          console.error("Error adding or updating medical service:", error);
          const msg =
            error?.response?.data?.message ||
            error?.response?.data?.error ||
            "Update failed";
          toast.error(msg);
        });
    } else {
      const hipId =
        facilityName || sessionStorage.getItem("facility_name") || "";
      if (!hipId) {
        toast.error(
          "Facility is not set. Please select a facility and try again.",
        );
        return;
      }

      const doctorForFee = doctorList.find(
        (doctor) => String(doctor.doctor_id) === String(data.doctor_id),
      );
      const baseName =
        data.is_doctor_appointment_charge && doctorForFee
          ? buildDoctorFeeServiceName(
              doctorForFee.name,
              data.fee_type || DOCTOR_FEE_TYPES.CONSULTATION,
            )
          : String(data.name || "").trim();
      const serviceNameToSave =
        data.is_doctor_appointment_charge &&
        doctorAllowsMultipleFees(doctorForFee)
          ? composeDoctorFeeServiceName(baseName, data.name_suffix)
          : baseName;

      // Same contract as ServiceAdditionOnTheGo / invoice "create on the fly"
      const payload = {
        name: serviceNameToSave,
        description: String(data.description || "").trim(),
        fee: Number(data.fee),
        unit: "1",
        hip_id: hipId,
        is_doctor_appointment_charge: Boolean(
          data.is_doctor_appointment_charge,
        ),
        ...(data.is_doctor_appointment_charge
          ? { doctor_id: String(data.doctor_id) }
          : {}),
        ...(data.expiry_date ? { expiry_date: data.expiry_date } : {}),
      };

      axios
        .post(
          `${REACT_APP_BACKEND_ROUTE_URL}add_new_medical_service/`,
          payload,
          requestConfig,
        )
        .then(async (res) => {
          if (res?.data?.status === "Success") {
            toast.success(res?.data?.message || "Service created successfully");
            await saveSlipDepartment(
              res?.data?.data?.medical_service_id ??
                res?.data?.services?.medical_service_id,
            );
            setModalOpen(false);
            reset();
            fetchServiceList({
              page: paginationModel.page + 1,
              limit: paginationModel.pageSize,
            });
          } else {
            toast.error(res?.data?.message || "Failed to create service");
          }
        })
        .catch((error) => {
          console.error("Error adding medical service:", error);
          const msg =
            error?.response?.data?.message ||
            error?.response?.data?.error ||
            "Failed to create service";
          toast.error(msg);
        });
    }
  };
  const getRowClassName = (params) => {
    return "custom-row";
  };
  const handleDeleteItem = (id) => {
    if (authToken !== "") {
      const requestData = {
        medical_service_id: id,
        hip_id: "HLTHTEK_DEV",
      };

      const requestConfig = {
        headers: headers,
      };

      axios
        .post(
          `${REACT_APP_BACKEND_ROUTE_URL}delete_medical_service/`,
          JSON.stringify(requestData),
          requestConfig,
        )
        .then(() => {
          toast.success("Deleted!");
          fetchServiceList({
            page: paginationModel.page + 1,
            limit: paginationModel.pageSize,
          });
        })
        .catch((error) => {
          console.error("Error deleting medical service:", error);
        });
    }
  };

  const handleToggleItem = (id, active) => {
    if (active) {
      setEditItemId(id);
      setDeactivationModalOpen(true);
    } else {
      toggleMedicalService(id, active, "");
    }
  };

  const toggleMedicalService = (id, active, reason) => {
    const action = active
      ? "delete_medical_service"
      : "activate_medical_service";

    const requestData = {
      medical_service_id: id,
      hip_id: sessionStorage.getItem("facility_name") || "",
      deactivation_reason: reason,
    };

    const requestConfig = {
      headers: headers,
    };

    axios
      .post(
        `${REACT_APP_BACKEND_ROUTE_URL}${action}/`,
        JSON.stringify(requestData),
        requestConfig,
      )
      .then(() => {
        toast.success(active ? "Deactivated!" : "Activated!");
        fetchServiceList({
          page: paginationModel.page + 1,
          limit: paginationModel.pageSize,
        });
      })
      .catch((error) => {
        console.error(
          `Error ${active ? "deactivating" : "activating"} medical service:`,
          error,
        );
      });
  };

  const handleDeactivationModalClose = () => {
    setDeactivationModalOpen(false);
    setDeactivationReason("");
    setValue1("description", "");
  };

  const handleDeactivateConfirm = (data) => {
    console.log(data);
    setDeactivationModalOpen(false);
    toggleMedicalService(editItemId, true, data.description);
    setDeactivationReason(data.description);
    setValue1("description", "");
  };

  const handleRowSelect = (id) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id],
    );
  };

  const handleSelectAll = () => {
    if (selectedRows.length === getCurrentPageData().length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(getCurrentPageData().map((row) => row.id));
    }
  };

  const getCurrentPageData = () => {
    return serviceList;
  };

  const totalPages = servicePagination.total_pages;

  const generatePdf = () => {
    const doc = new jsPDF();

    // const tableColumns = [
    //   "ID",
    //   "Name",
    //   "Description",
    //   "Fee",
    //   "Added on",
    //   "Expiry date",
    //   "Status",
    // ];
    doc.autoTable({
      head: [
        [
          "ID",
          "Name",
          "Description",
          "Fee",
          "Added on",
          "Expiry date",
          "Status",
        ],
      ],
      body: serviceList.map((item) => [
        item.id,
        item.name,
        item.description,
        item.fee,
        item.added_on,
        item.expiry_date || "\u2014",
        item.status,
      ]),
    });
    doc.save("Services_list.pdf");
  };

  const generateCsvData = () => {
    return serviceList.map((item) => ({
      ID: item.id,
      Name: item.name,
      Description: item.description,
      Fee: item.fee,
      "Added on": item.added_on,
      "Expiry date": item.expiry_date || "\u2014",
      Status: item.status,
    }));
  };
  // const style = {
  //   position: "absolute",
  //   top: "50%",
  //   left: "50%",
  //   transform: "translate(-50%, -50%)",
  //   width: { xs: "95%", sm: "90%", md: "80%", lg: "75%" },
  //   height: { xs: "90%", sm: "85%", md: "80%" },
  //   maxHeight: { xs: "92dvh", sm: "90dvh", md: "90vh" },
  //   bgcolor: "background.paper",
  //   boxShadow: 24,
  //   borderRadius: "8px",
  //   display: "flex",
  //   flexDirection: "column",
  //   overflow: "hidden",
  //   overflowX: "hidden",
  // };

  const modalHeaderHeight = 60;
  const modalFooterHeight = 70;

  return (
    <>
      <Dialog
        open={isDeactivationModalOpen}
        onClose={handleDeactivationModalClose}
        style={{
          // backdropFilter: "blur(5px)", // Applies blur to the background
          backgroundColor: "rgba(0, 0, 0, 0.5)", // Optional: Adds a semi-transparent overlay
        }}
      >
        <DialogTitle>Deactivate Medical Service</DialogTitle>
        <DialogContent>
          <form
            onSubmit={handleSubmit1((data) => {
              handleDeactivateConfirm({
                description: deactivationReason,
              });
            })}
          >
            <div className="col-12">
              <label htmlFor="description" className="required-label">
                Reason for Deactivation
              </label>
              <div className="form-group">
                <textarea
                  className="form-control"
                  placeholder="Enter Reason"
                  name="description"
                  {...register1("description", {
                    required: "Reason for deactivation is required",
                  })}
                />
                {errors1.description && (
                  <p className="error-message">{errors1.description.message}</p>
                )}
              </div>
            </div>
            <DialogActions>
              <button
                onClick={handleDeactivationModalClose}
                type="button"
                className="btn btn__secondary btn__rounded btn__block mt-2"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn__secondary btn__rounded btn__block mt-2"
              >
                Deactivate
              </button>
            </DialogActions>
          </form>
        </DialogContent>
      </Dialog>

      <Modal
        open={isModalOpen}
        onClose={handleModalClose}
        style={{
          // backdropFilter: "blur(5px)", // Applies blur to the background
          backgroundColor: "rgba(0, 0, 0, 0.5)", // Optional: Adds a semi-transparent overlay
        }}
      >
        <Box
          sx={{
            width: {
              xs: "100%",
              sm: "75%",
              md: "75%",
              lg: "40%",
              xl: "30%",
            },
            p: 2,
            mt: 3,
            mx: "auto",
            bgcolor: "background.paper",
            borderRadius: "10px",
            padding: 0,
            display: "flex",
            flexDirection: "column",
            maxHeight: "calc(100vh - 48px)",
            overflow: "hidden",
          }}
          // sx={style}
        >
          <Box
            sx={{
              // display: "flex",
              // justifyContent: "space-between",
              // alignItems: "center",
              // bgcolor: "background.paper",
              // p: 1,
              // borderRadius: 1,
              backgroundRepeat: "no-repeat",
              backgroundSize: "cover",
              // backgroundImage: `url(${Icons.modal_blue})`,
              backgroundColor: "#213360",
              height: { xs: 50, sm: modalHeaderHeight },
              // borderBottom: "1px solid #ddd",
              borderBottom: "1px solid rgba(255,255,255,0.15)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: { xs: 1, sm: 2 },
              borderTopLeftRadius: "8px",
              borderTopRightRadius: "8px",
              flexShrink: 0,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                // fontSize: { xs: "1.1rem", sm: "1.25rem" },
                fontSize: "20px",
                fontWeight: 550,
                color: "#FFFFFF",
              }}
            >
              {editItemId ? "Edit Service" : "Add New Service"}
            </Typography>
            <CLoseIcon
              onClick={handleModalClose}
              style={{
                width: "20px",
                height: "20px",
                cursor: "pointer",
                transition: "all 0.3s ease",
                "&:hover": {
                  border: "1px solid #000",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                  transform: "scale(1.1)",
                },
              }}
            />
          </Box>
          {/* <Box sx={{ bgcolor: "#ffffff", p: 2, borderRadius: 1 }}> */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              bgcolor: "background.paper",
              p: 2,
              borderRadius: 1,
              width: "100%",
              flex: 1,
              minHeight: 0,
              overflow: "hidden",
            }}
          >
            <form
              onSubmit={handleSubmit(handleSaveItem)}
              style={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
                height: "100%",
                minHeight: 0,
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflowY: "auto",
                  overflowX: "hidden",
                  pr: 0.5,
                }}
              >
                <div className="col-12" style={{ marginBottom: "0.5rem" }}>
                  <div
                    className="form-group d-flex align-items-center"
                    style={{ gap: "0.5rem" }}
                  >
                    <input
                      type="checkbox"
                      id="is_doctor_appointment_charge"
                      name="is_doctor_appointment_charge"
                      {...register("is_doctor_appointment_charge")}
                      disabled={Boolean(editItemId && isNameLocked)}
                      style={{ width: "auto", margin: "0" }}
                    />
                    <label
                      htmlFor="is_doctor_appointment_charge"
                      style={{ margin: "0" }}
                    >
                      This is a doctor consultation / appointment charge
                    </label>
                  </div>
                </div>

                {isDoctorAppointmentCharge && (
                  <div className="col-12" style={{ marginBottom: "0.5rem" }}>
                    <label htmlFor="doctor_id" className="required-label">
                      Doctor{" "}
                    </label>
                    <div
                      className="form-group"
                      style={{ position: "relative", zIndex: 2 }}
                    >
                      <Controller
                        name="doctor_id"
                        control={control}
                        rules={{
                          validate: (value, formValues) =>
                            !formValues.is_doctor_appointment_charge ||
                            !!value ||
                            "Doctor is required for an appointment charge",
                        }}
                        render={({ field }) => {
                          const selectedDoctor =
                            doctorList.find(
                              (doctor) =>
                                String(doctor.doctor_id) ===
                                String(field.value),
                            ) || null;
                          return (
                            <Autocomplete
                              id="doctor_id"
                              options={doctorList}
                              loading={isDoctorListLoading}
                              disabled={
                                isDoctorListLoading ||
                                Boolean(editItemId && isNameLocked)
                              }
                              value={selectedDoctor}
                              onChange={(_, doctor) => {
                                field.onChange(
                                  doctor ? String(doctor.doctor_id) : "",
                                );
                              }}
                              onBlur={field.onBlur}
                              getOptionLabel={(doctor) =>
                                `${doctor.name ?? doctor.doctor_id}${
                                  doctor.department
                                    ? ` — ${doctor.department}`
                                    : ""
                                }`
                              }
                              isOptionEqualToValue={(option, value) =>
                                String(option.doctor_id) ===
                                String(value.doctor_id)
                              }
                              disablePortal
                              ListboxProps={{
                                style: {
                                  maxHeight: "min(420px, calc(100vh - 260px))",
                                },
                              }}
                              componentsProps={{
                                paper: {
                                  sx: {
                                    maxHeight:
                                      "min(440px, calc(100vh - 240px))",
                                    boxShadow: "0 4px 16px rgba(0,0,0,0.16)",
                                  },
                                },
                                popper: {
                                  // Field is near the top of the form — open downward.
                                  placement: "bottom-start",
                                  modifiers: [
                                    { name: "flip", enabled: false },
                                    {
                                      name: "preventOverflow",
                                      enabled: true,
                                      options: {
                                        altAxis: true,
                                        padding: {
                                          top: 8,
                                          bottom: 8,
                                          left: 4,
                                          right: 4,
                                        },
                                        boundary: "clippingParents",
                                      },
                                    },
                                  ],
                                },
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  size="small"
                                  placeholder={
                                    isDoctorListLoading
                                      ? "Loading doctors..."
                                      : doctorList.length
                                        ? "Select Doctor"
                                        : "No doctors available"
                                  }
                                  error={Boolean(errors.doctor_id)}
                                  sx={{
                                    "& .MuiOutlinedInput-root": {
                                      backgroundColor:
                                        editItemId && isNameLocked
                                          ? "#f0f0f0"
                                          : "#fff",
                                    },
                                  }}
                                />
                              )}
                            />
                          );
                        }}
                      />
                      {errors.doctor_id && (
                        <p className="error-message">
                          {errors.doctor_id.message}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {isDoctorAppointmentCharge && (
                  <div className="col-12" style={{ marginBottom: "0.5rem" }}>
                    <label className="required-label">Fee Type</label>
                    <Controller
                      name="fee_type"
                      control={control}
                      defaultValue={DOCTOR_FEE_TYPES.CONSULTATION}
                      rules={{
                        validate: (value, formValues) =>
                          !formValues.is_doctor_appointment_charge ||
                          !!value ||
                          "Fee type is required",
                      }}
                      render={({ field }) => (
                        <RadioGroup
                          row
                          aria-label="Fee type"
                          name={field.name}
                          value={field.value || DOCTOR_FEE_TYPES.CONSULTATION}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                        >
                          <FormControlLabel
                            value={DOCTOR_FEE_TYPES.CONSULTATION}
                            control={<Radio size="small" />}
                            label="Consultation Fee"
                            disabled={Boolean(editItemId && isNameLocked)}
                          />
                          <FormControlLabel
                            value={DOCTOR_FEE_TYPES.FOLLOW_UP}
                            control={<Radio size="small" />}
                            label="Follow up Fee"
                            disabled={Boolean(editItemId && isNameLocked)}
                          />
                          <FormControlLabel
                            value={DOCTOR_FEE_TYPES.REPORT_VISIT}
                            control={<Radio size="small" />}
                            label="Report Visit Fee"
                            disabled={Boolean(editItemId && isNameLocked)}
                          />
                        </RadioGroup>
                      )}
                    />
                  </div>
                )}

                <div className="col-12" style={{ flex: "1" }}>
                  <label htmlFor="name" className="required-label">
                    Name{" "}
                  </label>
                  <div className="form-group">
                    {allowsNameSuffix ? (
                      <div
                        className="form-control"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          height: "auto",
                          minHeight: "38px",
                          padding: "2px 4px 2px 8px",
                        }}
                      >
                        <input
                          type="text"
                          id="name"
                          name="name"
                          {...register("name", { required: "Name is required" })}
                          readOnly
                          style={{
                            flex: "1 1 55%",
                            minWidth: 0,
                            border: "none",
                            outline: "none",
                            backgroundColor: "#f0f0f0",
                            cursor: "not-allowed",
                            padding: "4px 8px",
                            borderRadius: "4px",
                          }}
                        />
                        <input
                          type="text"
                          id="name_suffix"
                          placeholder="Suffix (optional)"
                          maxLength={NAME_SUFFIX_MAX_LENGTH}
                          {...register("name_suffix", {
                            maxLength: {
                              value: NAME_SUFFIX_MAX_LENGTH,
                              message: `Suffix must be ${NAME_SUFFIX_MAX_LENGTH} characters or fewer`,
                            },
                          })}
                          style={{
                            flex: "1 1 45%",
                            minWidth: "120px",
                            border: "none",
                            outline: "none",
                            background: "transparent",
                            padding: "4px 6px",
                          }}
                        />
                      </div>
                    ) : (
                      <input
                        type="text"
                        className="form-control"
                        id="name"
                        placeholder="Enter Name"
                        name="name"
                        {...register("name", { required: "Name is required" })}
                        readOnly={isNameLocked}
                        style={{
                          width: "100%",
                          backgroundColor: isNameLocked ? "#f0f0f0" : undefined,
                          cursor: isNameLocked ? "not-allowed" : "text",
                        }}
                      />
                    )}
                    {allowsNameSuffix ? (
                      <small style={{ color: "#666" }}>
                        Default name is fixed. Suffix is optional (max{" "}
                        {NAME_SUFFIX_MAX_LENGTH} characters).
                      </small>
                    ) : null}
                    {errors.name && (
                      <p className="error-message">{errors.name.message}</p>
                    )}
                    {errors.name_suffix && (
                      <p className="error-message">
                        {errors.name_suffix.message}
                      </p>
                    )}
                  </div>
                </div>

                <div className="col-12" style={{ flex: "1" }}>
                  <label htmlFor="description">Description </label>
                  <div className="form-group">
                    <textarea
                      className="form-control"
                      placeholder="Enter Description"
                      name="description"
                      {...register("description", {
                        // required: "Description is required",
                      })}
                      style={{ width: "100%" }}
                    />
                    {errors.description && (
                      <p className="error-message">
                        {errors.description.message}
                      </p>
                    )}
                  </div>
                </div>

                <div
                  className="col-12 d-flex"
                  style={{ marginBottom: "0.5rem", padding: "0" }}
                >
                  <div className="col-6" style={{ flex: "1" }}>
                    <label htmlFor="fee" className="required-label">
                      Fee{" "}
                    </label>
                    <div className="form-group">
                      <input
                        type="number"
                        className="form-control"
                        placeholder="Enter Fee"
                        name="fee"
                        onWheel={(e) => e.target.blur()}
                        {...register("fee", {
                          required: "Fee is required",
                          validate: (value, formValues) => {
                            const fee = Number(value);
                            if (Number.isNaN(fee) || fee < 0) {
                              return "Fee must be a valid non-negative number";
                            }
                            const feeType =
                              formValues.fee_type ||
                              inferFeeTypeFromServiceName(formValues.name);
                            const allowsZero =
                              feeType === DOCTOR_FEE_TYPES.FOLLOW_UP ||
                              feeType === DOCTOR_FEE_TYPES.REPORT_VISIT;
                            if (!allowsZero && fee === 0) {
                              return "Fee must be greater than 0";
                            }
                            return true;
                          },
                        })}
                        style={{ width: "100%" }}
                      />
                      {errors.fee && (
                        <p className="error-message">{errors.fee.message}</p>
                      )}
                    </div>
                  </div>
                  <div className="col-6" style={{ flex: "1" }}>
                    <label htmlFor="expiry_date">Expiry Date </label>
                    <div className="form-group">
                      <Controller
                        name="expiry_date"
                        control={control}
                        render={({ field }) => (
                          <ApiDatePickerField
                            id="expiry_date"
                            value={field.value || ""}
                            onChange={field.onChange}
                            className="form-control"
                          />
                        )}
                      />
                    </div>
                  </div>
                </div>

                {!isDoctorAppointmentCharge && (
                <div className="col-12" style={{ flex: "1" }}>
                  <label htmlFor="slip_department" className="required-label">
                    Slip Department{" "}
                  </label>
                  <div className="form-group">
                    <select
                      id="slip_department"
                      className="form-control"
                      value={deptChoice}
                      onChange={(e) => setDeptChoice(e.target.value)}
                      disabled={!slipDepts || Boolean(slipDepts.error)}
                      style={{ width: "100%" }}
                    >
                      <option value="" disabled>
                        {slipDepts ? "Select department" : "Loading…"}
                      </option>
                      {slipDepts?.medical.length > 0 && (
                        <optgroup label="Medical departments">
                          {slipDepts.medical.map((d) => (
                            <option key={`med:${d.id}`} value={`med:${d.id}`}>
                              {d.name}
                            </option>
                          ))}
                        </optgroup>
                      )}
                      {slipDepts?.physical.length > 0 && (
                        <optgroup label="Physical departments">
                          {slipDepts.physical.map((d) => (
                            <option key={`phys:${d.id}`} value={`phys:${d.id}`}>
                              {d.name}
                            </option>
                          ))}
                        </optgroup>
                      )}
                      <option value="new">+ Create new department</option>
                    </select>
                    {deptChoice === "new" && (
                      <input
                        type="text"
                        className="form-control mt-2"
                        placeholder="New department name"
                        value={newDeptName}
                        onChange={(e) => setNewDeptName(e.target.value)}
                        style={{ width: "100%" }}
                      />
                    )}
                    {slipDepts?.error && (
                      <p className="error-message">
                        Slip departments unavailable: {slipDepts.error}
                      </p>
                    )}
                  </div>
                </div>
                )}
              </Box>

              <Box
                className="col-12 d-flex"
                sx={{
                  flexShrink: 0,
                  gap: 1,
                  pt: 1.5,
                  mt: 0.5,
                  borderTop: "1px solid #E5EAF0",
                  backgroundColor: "#fff",
                  padding: "0",
                  marginBottom: "0.5rem",
                }}
              >
                <div className="col-6" style={{ flex: "1" }}>
                  <button
                    type="submit"
                    className="btn btn__secondary btn__block mt-2"
                    style={{
                      width: "100%",
                      marginBottom: "0.5rem",
                      borderRadius: "10px",
                      border: "1px solid #125386",
                    }}
                  >
                    SAVE
                  </button>
                </div>
                <div className="col-6" style={{ flex: "1" }}>
                  <button
                    onClick={handleModalClose}
                    type="button"
                    className="btn btn__secondary btn__block mt-2"
                    style={{
                      width: "100%",
                      borderRadius: "10px",
                      border: "1px solid #125386",
                    }}
                  >
                    CANCEL
                  </button>
                </div>
              </Box>
            </form>
          </Box>
          {/* </Box> */}
        </Box>
      </Modal>

      <SideNavbar />
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr auto", // One column for text, one for button
          alignItems: "center",
          padding: "12px",
          paddingLeft: "12px",
          paddingRight: "12px",
          gap: "20px", // Space between text and button
          marginBottom: "5px",
          // paddingX: { xs: 2, sm: 3 }, // Add padding for smaller screens
        }}
      >
        <p
          style={{
            fontSize: "32px",
            fontWeight: "bold",
            textAlign: "left",
            color: "#172935",
            margin: 0, // Remove default margin to align properly
          }}
        >
          Available Services
        </p>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <TextField
            size="small"
            placeholder="Search by Service Name"
            value={serviceSearchText}
            onChange={(e) => setServiceSearchText(e.target.value)}
            className="billing-search-field"
            sx={{
              minWidth: 260,
              "& .MuiOutlinedInput-root": {
                height: "42px",
                borderRadius: "10px",
              },
            }}
            InputProps={{
              startAdornment: (
                <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
              ),
            }}
          />
          <button
            onClick={handleAddItem}
            type="button"
            className="btn btn__secondary btn__rounded btn__block billing-header-action-btn"
            // variant="contained"
            // color="primary"
            style={{
              backgroundColor: "#0E3F66",
              borderRadius: "10px",
              fontSize: "14px",
              paddingX: { xs: 2, sm: 3 },
              paddingY: 1,
              textTransform: "none",
              height: "42px",
              // "&:hover": {
              //   backgroundColor: "#0C3555",
              // },
            }}
          >
            + Add Services
          </button>
        </Box>
      </Box>
      <div className="display-flex">
        {serviceList !== undefined && serviceList.length > 0 ? (
          <DataGrid
            style={{ maxHeight: "90vh" }}
            rows={serviceList}
            paginationMode="server"
            rowCount={servicePagination.total_records}
            paginationModel={paginationModel}
            onPaginationModelChange={(model) => setPaginationModel(model)}
            loading={serviceLoading}
            columns={[
              { field: "id", headerName: "ID", width: 150 },
              { field: "name", headerName: "Name", width: 200 },
              {
                field: "fee_type",
                headerName: "Fee Type",
                width: 150,
                valueGetter: (params) =>
                  getFeeTypeLabelFromServiceName(params.row?.name),
                renderCell: (params) => (
                  <span>
                    {getFeeTypeLabelFromServiceName(params.row?.name)}
                  </span>
                ),
              },
              { field: "description", headerName: "Description", width: 200 },
              { field: "fee", headerName: "Fee", width: 100 },
              {
                field: "added_on",
                headerName: "Added On",
                width: 150,
                renderCell: (params) => (
                  <span>
                    <DisplayDate value={params.row.added_on} emptyLabel="" />
                  </span>
                ),
              },
              {
                field: "expiry_date",
                headerName: "Expiry date",
                width: 150,
                renderCell: (params) => {
                  if (!params.row.expiry_date) return <span>—</span>;
                  const currentDate = new Date();
                  const expiryDate = new Date(params.row.expiry_date);
                  const isExpired = expiryDate < currentDate;

                  return (
                    <span style={{ color: isExpired ? "red" : "inherit" }}>
                      <DisplayDate
                        value={params.row.expiry_date}
                        emptyLabel="—"
                      />
                    </span>
                  );
                },
              },
              {
                field: "status",
                headerName: "Status",
                width: 120,
                renderCell: (params) => {
                  const isExpired = params.row.expired;
                  const isActive = params.row.active;

                  let statusColor = "";
                  let statusText = "";

                  if (!isExpired && isActive) {
                    statusColor = "green";
                    statusText = "Active";
                  } else {
                    statusColor = "red";
                    statusText = "Inactive";
                  }

                  return (
                    <span style={{ color: statusColor }}>{statusText}</span>
                  );
                },
              },
              {
                field: "edit",
                headerName: "Edit",
                width: 100,
                renderCell: (params) => (
                  <Tooltip title="Edit">
                    <EditIcon
                      onClick={() => handleEditItem(params.row)}
                      style={{ cursor: "pointer", color: "#0D233F" }}
                    />
                  </Tooltip>
                ),
              },
              {
                field: "toggle",
                headerName: "Action",
                width: 100,
                renderCell: (params) => (
                  <>
                    {params.row.expired ? (
                      <Tooltip title="Inactive">
                        <ToggleOffIcon
                          style={{ cursor: "not-allowed", color: "gray" }}
                        />
                      </Tooltip>
                    ) : params.row.active ? (
                      <Tooltip title="Active">
                        <ToggleOnIcon
                          onClick={() =>
                            handleToggleItem(
                              params.row.medical_service_id,
                              params.row.active,
                            )
                          }
                          style={{ cursor: "pointer", color: "#0D233F" }}
                        />
                      </Tooltip>
                    ) : (
                      <Tooltip title="Inactive">
                        <ToggleOffIcon
                          onClick={() =>
                            handleToggleItem(
                              params.row.medical_service_id,
                              params.row.active,
                            )
                          }
                          style={{ cursor: "pointer", color: "#0D233F" }}
                        />
                      </Tooltip>
                    )}
                  </>
                ),
              },
            ]}
            components={{
              Toolbar: () => (
                <Grid
                  container
                  spacing={2}
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{
                    mb: 2,
                  }}
                >
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={8}
                    sx={{ display: "flex", gap: 1 }}
                  >
                    <GridToolbarColumnsButton />
                    <GridToolbarFilterButton />
                    <GridToolbarDensitySelector />
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={4}
                    sx={{
                      display: "flex",
                      gap: 1,
                      justifyContent: { xs: "center", sm: "flex-end" },
                    }}
                  >
                    <Button
                      variant="text"
                      color="primary"
                      onClick={() => generatePdf()}
                      startIcon={<SaveAlt />}
                    >
                      Download PDF
                    </Button>
                    <CSVLink
                      data={generateCsvData()}
                      filename={"scan_share_records.csv"}
                      target="_blank"
                    >
                      <Button
                        variant="text"
                        color="primary"
                        startIcon={<SaveAlt />}
                      >
                        Download CSV
                      </Button>
                    </CSVLink>
                  </Grid>
                </Grid>
              ),
            }}
            pageSizeOptions={[5, 10]}
            getRowClassName={getRowClassName}
            sx={{
              "& .MuiDataGrid-root": {
                border: "1px solid #e0e0e0",
                borderRadius: "4px",
                fontFamily: "Arial, sans-serif",
              },
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#125386",
                color: "white",
                fontSize: "13px",
                fontWeight: "600",
                minHeight: "48px !important",
                "& .MuiDataGrid-columnHeaderTitle": {
                  color: "white",
                  fontWeight: "600",
                  fontSize: "13px",
                },
                "& .MuiDataGrid-columnHeader": {
                  borderRight: "1px solid rgba(255, 255, 255, 0.3)",
                  padding: "0 12px",
                },
                "& .MuiDataGrid-columnHeader:last-child": {
                  borderRight: "none",
                },
                "& .MuiDataGrid-iconButtonContainer": {
                  color: "white",
                },
                "& .MuiDataGrid-menuIcon": {
                  color: "white",
                },
                "& .MuiDataGrid-sortIcon": {
                  color: "white",
                },
                "& .MuiDataGrid-columnHeaderSortable": {
                  "& .MuiDataGrid-sortIcon": {
                    color: "white",
                  },
                },
                "& .MuiDataGrid-columnHeaderTitleContainer": {
                  "& .MuiDataGrid-sortIcon": {
                    color: "white",
                  },
                },
                "& .MuiSvgIcon-root": {
                  color: "white",
                },
              },
              "& .MuiDataGrid-row": {
                backgroundColor: "white",
                minHeight: "40px !important",
                "&:nth-of-type(even)": {
                  backgroundColor: "white",
                },
                "&:hover": {
                  backgroundColor: "#f8f9fa",
                },
                "&.Mui-selected": {
                  backgroundColor: "#e3f2fd",
                  "&:hover": {
                    backgroundColor: "#e3f2fd",
                  },
                },
                borderBottom: "1px solid #e0e0e0",
              },
              "& .MuiDataGrid-cell": {
                borderRight: "1px solid #e0e0e0",
                fontSize: "12px",
                color: "#374151",
                padding: "8px 12px",
                minHeight: "40px !important",
                "&:last-child": {
                  borderRight: "none",
                },
                "&:focus": {
                  outline: "none",
                },
              },
              "& .MuiDataGrid-cellContent": {
                fontSize: "12px",
                color: "#374151",
                fontWeight: "400",
              },
              "& .MuiDataGrid-footerContainer": {
                backgroundColor: "#f8f9fa",
                borderTop: "1px solid #e0e0e0",
                minHeight: "52px",
              },
              "& .MuiTablePagination-root": {
                color: "#374151",
                fontSize: "12px",
              },
              "& .MuiDataGrid-toolbarContainer": {
                backgroundColor: "#f8f9fa",
                borderBottom: "1px solid #e0e0e0",
                padding: "8px 16px",
              },
            }}
          />
        ) : (
          <NoDataPage />
        )}
      </div>
    </>
  );
};

export default Item;
